import React, { useEffect, useState } from "react";
import TitleStripe from "../../common/TitleStripe";
import {
  calculateRiskScore,
  formatDocument,
  formatToReal,
  getLetterScore,
  getScoreLegends,
  maskCEP,
} from "../../../utils/utils";
import { EvaluationScorePJ } from "../../common/query/EvaluationScorePJ";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import NoOcurrence from "../../common/query/NoOcurrence";
import Protestos from "../../common/query/Protestos";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import ChequesSustados from "../../common/query/ChequesSustados";
import AvisoCard from "../../common/query/AvisoCard";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";
import ReactApexChart from "react-apexcharts";
import ScoreRating from "./ScoreRating";
import { Badge, Modal, Table } from "flowbite-react";
import QuadroSocietario from "../../common/query/QuadroSocietario";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import Socios from "../../common/query/Socios";
import AcoesCiveis from "../../common/query/AcoesCiveis";
import DecisionBanner from "../DecisionBanner";
import StreetView from "../../common/query/StreetView";
import { HiOutlineEye } from "react-icons/hi";
import ModalReceitaFederal from "../../modals/ModalReceitaFederal";

const formatYYYYMMToYY = (yyyyMM) => {
  if (!yyyyMM) return "–";
  const [y, m] = yyyyMM.split("-");
  return `${m}/${String(y).slice(-2)}`;
};

const firstNonEmptySeries = (src, keys) => {
  for (const k of keys) {
    const arr = src?.[k];
    if (Array.isArray(arr) && arr.length) return arr;
  }
  return [];
};

const DefineNegocio = ({ data, dataFull }) => {
  const [riskScore, setRiskScore] = useState(0);
  const [openMap, setOpenMap] = useState(false);

  // Fallbacks para novos campos vindos do formatador
  const sociosEmpresa = data?.socios;
  const administradoresEmpresa = data?.administradores;

  const participacoesHierarquico =
    data?.participacoesSociosAdmOutrasEmpresas || [];
  const participacoesEmpresaConsultada =
    dataFull?.participacoesEmpresaConsultada ||
    data?.participacaoEmEmpresas ||
    [];

  const faturamentoPresumido = dataFull?.faturamentoPresumido;

  useEffect(() => {
    setRiskScore(
      calculateRiskScore(
        +data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal || 0,
        +data?.resumoConsulta?.protestos?.quantidadeTotal || 0,
        +data?.score?.score,
        +data?.score?.probabilidade
      )
    );
  }, [data]);

  const fullAddress = [
    data?.dadosCadastrais?.endereco,
    data?.dadosCadastrais?.bairro,
    data?.dadosCadastrais?.cidade && data?.dadosCadastrais?.uf
      ? `${data?.dadosCadastrais?.cidade} - ${data?.dadosCadastrais?.uf}`
      : data?.dadosCadastrais?.cidade || data?.dadosCadastrais?.uf,
    maskCEP(data?.dadosCadastrais?.cep),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div>
      <DecisionBanner compact={true} flag={data?.decisao?.flag} />
      <TitleStripe>Dados Cadastrais</TitleStripe>

      <div className="mt-4 grid grid-cols-2 gap-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-sm">
        <div className="space-y-4">
          <div>
            <div className="text-gray-500 text-xs uppercase">Razão Social</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.razaoSocial || "Indisponível"}
            </div>
            <ModalReceitaFederal cnpj={data?.dadosCadastrais?.cnpj} />
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Nome Fantasia</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.nomeFantasia || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              Natureza Jurídica
            </div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.naturezaJuridica || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">CNPJ/MF</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.cnpj || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              CNAE Principal
            </div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.ramoAtividade || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Fundação</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.dataFundacao || "Indisponível"}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-gray-500 text-xs uppercase">
              Situação do CNPJ
            </div>
            <div
              className={`inline-block px-2 py-1 text-xs font-semibold rounded-md text-white ${
                (data?.dadosCadastrais?.situacao || "").toLowerCase() ===
                "ativo"
                  ? "bg-green-600"
                  : "bg-red-600"
              }`}
            >
              {data?.dadosCadastrais?.situacao || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Endereço</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.endereco || "Indisponível"}
              {fullAddress && (
                <div
                  className="flex items-center text-xs gap-1 text-blue-600 cursor-pointer"
                  onClick={() => setOpenMap(true)}
                >
                  <span>- Ver fotos do local</span>
                  <HiOutlineEye className="text-lg" />
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Bairro</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.bairro || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Cidade/UF</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.cidade || "Indisponível"} /{" "}
              {data?.dadosCadastrais?.uf || "--"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">CEP</div>
            <div className="font-medium text-gray-900">
              {maskCEP(data?.dadosCadastrais?.cep) || "Indisponível"}
            </div>
          </div>
        </div>
      </div>

      {/* Avaliação preliminar */}
      <div className="mb-4">
        <TitleStripe>Avaliação preliminar de crédito</TitleStripe>
      </div>

      {data?.score?.score == 2 ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
          <p className="font-medium">
            Não foi possível calcular o score desta empresa.
          </p>
          <p className="mt-1">
            É necessário verificar as outras informações disponíveis no
            relatório para uma análise completa.
          </p>
        </div>
      ) : (
        <EvaluationScorePJ
          data={data}
          score={data?.score?.score}
          probability={+data?.score?.probabilidade * 100}
          type="CNPJ"
          hasIncome={false}
        />
      )}

      {dataFull?.scoreRating?.statusRetorno == 1 && (
        <ScoreRating score={dataFull?.scoreRating} block={false} />
      )}

      {/* Quadro societário */}
      <div>
        <TitleStripe className="mb-5">Quadro Societário</TitleStripe>
        {sociosEmpresa?.length > 0 ? (
          <Socios socios={sociosEmpresa} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* Administradores */}
      <div>
        <TitleStripe className="mb-5">Administradores</TitleStripe>
        {administradoresEmpresa?.length > 0 ? (
          <Socios isAdm={true} socios={administradoresEmpresa} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* Participações hierárquicas: cada sócio/adm e suas empresas */}
      <div>
        <TitleStripe className="mb-5">
          Participações de Sócios/Administradores em Outras Empresas
        </TitleStripe>
        {participacoesHierarquico?.length > 0 ? (
          <div className="space-y-4">
            {participacoesHierarquico.map((node, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-3 bg-white"
              >
                <div className="text-sm font-semibold">
                  {node?.socio?.nome} — {node?.socio?.cpf}
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  {node?.socio?.cargo || "Sócio/Adm"}
                </div>

                {node?.participacoes?.length ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <Table.Head>
                        <Table.HeadCell className="text-left text-black text-xs px-1">
                          Empresa
                        </Table.HeadCell>
                        <Table.HeadCell className="text-left text-black text-xs px-1">
                          CNPJ
                        </Table.HeadCell>
                        <Table.HeadCell className="text-left text-black text-xs px-1">
                          Participação
                        </Table.HeadCell>
                        <Table.HeadCell className="text-left text-black text-xs px-1">
                          Entrada
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {node.participacoes.map((p, i2) => (
                          <Table.Row key={i2}>
                            <Table.Cell className="text-left text-black text-xs px-1">
                              {p?.empresa || "—"}
                            </Table.Cell>
                            <Table.Cell className="text-left text-black text-xs px-1">
                              {p?.cnpj ? formatDocument(p?.cnpj) : "—"}
                            </Table.Cell>
                            <Table.Cell className="text-left text-black text-xs px-1">
                              {p?.participacaoEmPorcentagem || "—"}
                            </Table.Cell>
                            <Table.Cell className="text-left text-black text-xs px-1">
                              {p?.dataEntrada || "—"}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    Sem participações.
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* Faturamento Presumido */}
      {faturamentoPresumido &&
        (faturamentoPresumido?.valor != null ||
          faturamentoPresumido?.series?.length > 0) && (
          <div>
            <TitleStripe className="mb-4">Faturamento Presumido</TitleStripe>

            <div className="mt-2 font-semibold">
              Estimado:{" "}
              <Badge className="w-fit inline" color="gray">
                {faturamentoPresumido?.valor != null
                  ? formatToReal(faturamentoPresumido.valor)
                  : "–"}
              </Badge>
            </div>

            {faturamentoPresumido?.series?.length > 0 && (
              <div className="mt-2 w-full rounded-lg border border-gray-300 overflow-auto">
                <Table>
                  <Table.Head>
                    {faturamentoPresumido.series.map((pt, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center text-black text-xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatYYYYMMToYY(pt.mes)}
                      </Table.HeadCell>
                    ))}
                  </Table.Head>
                  <Table.Body className="divide-y">
                    <Table.Row>
                      {faturamentoPresumido.series.map((pt, idx) => (
                        <Table.Cell
                          key={idx}
                          className="text-center text-black text-xs px-1"
                        >
                          {pt?.valor != null ? formatToReal(pt.valor) : "–"}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>
            )}

            <div className="mt-3 text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
              Estimativa baseada em sinais comportamentais e declarações
              públicas disponíveis para o CNPJ. Use com cautela.
            </div>
          </div>
        )}

      {/* Indicadores comportamentais */}
      {data?.creditoObtido && data?.creditoObtido?.series?.length > 0 && (
        <div>
          <TitleStripe className="mb-4">Crédito Obtido</TitleStripe>
          {data?.creditoObtido ? (
            <>
              <div className="mt-4 font-semibold">
                Pontuação:{" "}
                <Badge className="w-fit inline" color="gray">
                  {data?.creditoObtido?.pontuacao != null
                    ? formatToReal(data?.creditoObtido?.pontuacao * 1000)
                    : "–"}
                </Badge>
              </div>

              <div className="mt-2 w-full rounded-lg border border-gray-300 overflow-auto">
                <Table>
                  <Table.Head>
                    {data?.creditoObtido?.series?.map((pt, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center text-black text-xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatYYYYMMToYY(pt.mes)}
                      </Table.HeadCell>
                    ))}
                  </Table.Head>
                  <Table.Body className="divide-y">
                    <Table.Row>
                      {data?.creditoObtido?.series?.map((pt, idx) => (
                        <Table.Cell
                          key={idx}
                          className="text-center text-black text-xs px-1"
                        >
                          {pt?.valor != null
                            ? formatToReal(pt.valor * 1000)
                            : "–"}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>

              <div className="mt-3 text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
                Descrição: Valor total de crédito já concedido ao consumidor ou
                empresa pelas instituições consultadas. Representa o quanto foi
                efetivamente aprovado em financiamentos, compras parceladas ou
                outras operações de crédito.
              </div>
            </>
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.pagamentoPontual && data?.pagamentoPontual?.series?.length > 0 && (
        <div>
          <TitleStripe className="mb-4">Pagamento Pontual</TitleStripe>
          {data?.pagamentoPontual ? (
            <>
              <div className="mt-4 font-semibold">
                Pontuação:{" "}
                <Badge className="w-fit inline" color="gray">
                  {data?.pagamentoPontual?.pontuacao ?? "–"}
                </Badge>
              </div>

              <div className="border border-gray-300 rounded-lg mt-2">
                <Table>
                  <Table.Head>
                    {data?.pagamentoPontual?.series?.map((pt, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center text-black text-xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatYYYYMMToYY(pt.mes)}
                      </Table.HeadCell>
                    ))}
                  </Table.Head>
                  <Table.Body className="divide-y">
                    <Table.Row>
                      {data?.pagamentoPontual?.series?.map((pt, idx) => {
                        const n = parseFloat(pt?.valor);
                        return (
                          <Table.Cell
                            key={idx}
                            className="text-center text-black text-2xs px-1"
                          >
                            {!isNaN(n) ? `${n}%` : "–"}
                          </Table.Cell>
                        );
                      })}
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>

              <div className="flex justify-center items-center mt-3">
                <span className="text-green-500 font-semibold">100%</span> :
                Pagamentos Pontuais |{" "}
                <span className="text-red-500 font-semibold ml-1">0%</span> :
                Pagamentos não pontuais
              </div>
            </>
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.pagamentoAtrasado &&
        data?.pagamentoAtrasado?.atraso_medio_dias?.length > 0 && (
          <div>
            <TitleStripe className="mb-5">Percentual em atrasos</TitleStripe>

            {data?.pagamentoAtrasado ? (
              <>
                <div className="w-full rounded-lg border border-gray-300 overflow-x-auto">
                  <Table>
                    <Table.Head>
                      <Table.HeadCell
                        className="text-left text-black text-2xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        Tipo
                      </Table.HeadCell>

                      {firstNonEmptySeries(data?.pagamentoAtrasado, [
                        "dias_6_15",
                        "dias_16_30",
                        "dias_31_60",
                        "mais_60",
                        "atraso_medio_dias",
                      ])?.map((pt, idx) => (
                        <Table.HeadCell
                          key={idx}
                          className="text-center text-black text-2xs px-1"
                          style={{ background: "#EBF5FF" }}
                        >
                          {formatYYYYMMToYY(pt.mes)}
                        </Table.HeadCell>
                      ))}
                    </Table.Head>

                    <Table.Body className="divide-y">
                      {[
                        ["Período 6 a 15 dias", "dias_6_15"],
                        ["Período 16 a 30 dias", "dias_16_30"],
                        ["Período 31 a 60 dias", "dias_31_60"],
                        ["Mais de 60 dias", "mais_60"],
                        ["Atraso médio (dias)", "atraso_medio_dias"],
                      ].map(([label, key], i) => {
                        const series = data?.pagamentoAtrasado?.[key] || [];
                        if (!Array.isArray(series) || series.length === 0)
                          return null;

                        return (
                          <Table.Row key={i}>
                            <Table.Cell className="text-left text-black text-2xs font-medium px-1">
                              {label}
                            </Table.Cell>

                            {series.map((pt, idx) => {
                              const n = parseFloat(pt?.valor);
                              const isDias = key === "atraso_medio_dias";
                              return (
                                <Table.Cell
                                  key={idx}
                                  className="text-center text-black text-2xs px-1"
                                >
                                  {!isNaN(n)
                                    ? isDias
                                      ? `${n}d`
                                      : `${n}%`
                                    : "–"}
                                </Table.Cell>
                              );
                            })}
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                </div>

                <div className="flex justify-center items-center mt-3">
                  <span className="text-red-500 font-semibold">100%</span> :
                  Pagamentos em atrasos |{" "}
                  <span className="text-green-500 font-semibold ml-1">0%</span>{" "}
                  : Pagamentos em dia
                </div>
              </>
            ) : (
              <NoOcurrence />
            )}
          </div>
        )}

      {data?.compromissos && data?.compromissos?.series?.length > 0 && (
        <div>
          <TitleStripe className="mb-5">
            Compromissos (quantidade de títulos)
          </TitleStripe>

          {data?.compromissos ? (
            <>
              <div className="border border-gray-300 rounded-lg mt-2 overflow-x-auto">
                <Table>
                  <Table.Head>
                    {data?.compromissos?.series?.map((pt, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center text-black text-2xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatYYYYMMToYY(pt.mes)}
                      </Table.HeadCell>
                    ))}
                  </Table.Head>

                  <Table.Body className="divide-y">
                    <Table.Row>
                      {data?.compromissos?.series?.map((pt, idx) => {
                        const n = parseFloat(pt?.valor);
                        return (
                          <Table.Cell
                            key={idx}
                            className="text-center text-black text-2xs px-1"
                          >
                            {!isNaN(n) ? n * 100 : "–"}
                          </Table.Cell>
                        );
                      })}
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>

              <div className="mt-3 text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
                Descrição: Número total de obrigações financeiras registradas no
                nome do consumidor ou empresa, como boletos, duplicatas, carnês
                ou contratos de crédito. Indica a quantidade de compromissos
                ativos.
              </div>
            </>
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {/* Negativas, protestos, ações, cheques */}
      <div>
        <TitleStripe className="mb-4">Negativações Registradas</TitleStripe>

        {data?.pendenciasFinanceiras?.length > 0 ? (
          <PendenciasFinanceiras
            quantidadeTotal={
              data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal
            }
            valorTotal={data?.resumoConsulta?.pendenciasFinanceiras?.valorTotal}
            pendenciasFinanceiras={data?.pendenciasFinanceiras}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Protestos</TitleStripe>
        {data?.protestos?.length > 0 ? (
          <Protestos
            quantidadeTotal={data?.resumoConsulta?.protestos?.quantidadeTotal}
            valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
            protestos={data?.protestos}
            temVencimento={false}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">⚖️ Ações cíveis</TitleStripe>
        {data?.acoesCiveis?.length > 0 ? (
          <AcoesCiveis
            quantidadeTotal={data?.resumoConsulta?.acoesCiveis?.quantidadeTotal}
            acoesCiveis={data?.acoesCiveis}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Cheques sem fundo</TitleStripe>
        {data?.chequesSemFundo?.length > 0 ? (
          <ChequesSemFundos
            quantidadeTotal={
              data?.resumoConsulta?.chequesSemFundo?.quantidadeTotal
            }
            chequesSemFundos={data?.chequesSemFundo}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Cheques sustados</TitleStripe>
        {data?.chequesSustados?.length > 0 ? (
          <ChequesSustados
            quantidadeTotal={
              data?.resumoConsulta?.chequesSustados?.quantidadeTotal
            }
            chequesSustados={data?.chequesSustados}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* Histórico de consultas */}
      <div>
        <TitleStripe className="mb-5">Histórico de consultas</TitleStripe>

        {data?.historicoConsultas?.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex-1">
              <HistoricoConsultas
                historicoConsultas={data?.historicoConsultas}
              />
            </div>
            {/* gráfico opcional comentado */}
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <Modal show={openMap} onClose={() => setOpenMap(false)} size="5xl">
        <Modal.Header>Localização</Modal.Header>
        <Modal.Body>
          {fullAddress ? (
            <StreetView address={fullAddress} height={460} />
          ) : (
            <div className="p-3 text-sm text-gray-500">
              Endereço indisponível.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DefineNegocio;
